import React, {
  createContext,
  useContext,
  ReactNode,
  useMemo,
} from "react";
import { useAppSelector } from "../../../services/hooks";
import { useGetSelfBookingPolicyQuery } from "../../../services/api/user.api";
import { userAppointmentsSelector } from "../../../services/selectors/user.selectors";
import { IAppointment } from "../types/appointments.types";

// Interface
interface AppointmentsContextValue {
  /**
   * Whether this patient may book for themselves. False while a device protocol is in force: the
   * treatment needs a technician and a machine, so the clinic schedules it.
   */
  canSelfBook: boolean;
  allAppointments: IAppointment[];
  searchedAppointments: IAppointment[]; // Appointments filtered by search term
  isLoading: boolean;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  filterByDateRange: (start: Date, end: Date) => IAppointment[];
  filterByPatientSearch: (searchTerm?: string) => IAppointment[];
  filterAppointments: (options: {
    dateRange?: { start: Date; end: Date };
    fundingSourceIds?: number[];
    therapistSearch?: string;
  }) => IAppointment[];
}

const AppointmentsContext = createContext<AppointmentsContextValue | undefined>(
  undefined
);

interface AppointmentsProviderProps {
  children: ReactNode;
}

// Helper to format date as YYYY-MM-DD
const formatDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Calculate duration in minutes from time strings
const calculateDuration = (startTime: string, endTime: string): number => {
  const [startHours, startMinutes] = startTime.split(":").map(Number);
  const [endHours, endMinutes] = endTime.split(":").map(Number);
  const startTotalMinutes = startHours * 60 + startMinutes;
  const endTotalMinutes = endHours * 60 + endMinutes;
  return endTotalMinutes - startTotalMinutes;
};

/**
 * Appointments Context Provider - Client Version
 * 
 * Uses appointments from Redux store (loaded on login via getAppointmentsByUserId)
 * No need for month-based caching since all appointments are already loaded
 */
export const AppointmentsProvider: React.FC<AppointmentsProviderProps> = ({
  children,
}) => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const { data: selfBooking } = useGetSelfBookingPolicyQuery();

  // Get appointments from Redux store (already loaded)
  const userAppointments = useAppSelector(userAppointmentsSelector) || [];

  // Transform appointments to match our IAppointment interface
  const allAppointments = useMemo(() => {
    return userAppointments
      .filter(a => a.appointments?.appointmentDate && a.appointments?.statusId !== 0)
      .map((apt: any): IAppointment => {
        const app = apt.appointments || {};
        const appDate = new Date(app.appointmentDate || Date.now());
        const dateStr = formatDateString(appDate);
        const source = apt.source || app.source;
        
        return {
          id: app.id,
          patientId: app.usersId,
          usersId: app.usersId,
          patientName: app.users?.fullName || "",
          users: app.users ? {
            id: app.users.id,
            name: app.users.fullName,
            firstName: app.users.firstName,
            lastName: app.users.lastName,
            guid: app.users.guid,
          } : undefined,
          usersGuid: app.usersGuid,
          therapistId: app.therapistsId,
          therapistsId: app.therapistsId,
          therapistName: apt.therapistName || "",
          therapist: app.therapistsId ? {
            id: app.therapistsId,
            name: apt.therapistName || "",
          } : undefined,
          therapistsGuid: app.therapistsGuid,
          companyId: app.companyId || 0,
          companyName: apt.companyName || "",
          date: dateStr,
          time: (app.startTime || "").split(':').slice(0, 2).join(':'),
          endTime: (app.endTime || "").split(':').slice(0, 2).join(':'),
          duration: (app.endTime && app.startTime)
            ? calculateDuration(app.startTime, app.endTime)
            : 60,
          mediaId: app.mediaId,
          mediaName: apt.mediaName || "",
          notes: app.appointmentReport || "",
          comments: app.comments || "",
          statusId: app.statusId,
          treatmentGroupId: app.treatmentGroupId,
          treatmentGroupName: app.treatmentGroupName,
          appointmentsProgramsId: app.appointmentsProgramsId,
          fundingSourcesId: apt.fundingSourcesId,
          fundingSourceName: apt.fundingSourceName || "",
          treatmentRoomId: app.treatmentRoomId,
          treatmentRoomName: app.treatmentRoomName || "",
          device: apt.device ?? null,
          treatmentProgramName: app.appointmentsPrograms?.name,
          treatmentProgramTotalSessions: app.appointmentsPrograms?.countOfAppointments,
          treatmentProgramCurrentSession: apt.treatmentProgramCurrentSession,
          treatmentTypeId: app.treatmentTypeId,
          treatmentTypeName: app.treatmentTypeName,
          // A device session names no therapist and belongs to no group, but it is a treatment all
          // the same — the machine is what it is booked against, and the card shows its name.
          source: (source === 'google_calendar' || source === 'other') ? source : ((!app.therapistsId && !app.treatmentGroupId && !apt.device) ? 'other' : 'regular'),
          avatar: apt.avatar,
        };
      });
  }, [userAppointments]);



  // Calculate searched appointments derived from allAppointments
  const searchedAppointments = useMemo(() => {
    if (!searchTerm.trim()) {
      return allAppointments;
    }
    const lowerTerm = searchTerm.toLowerCase();
    return allAppointments.filter(apt => 
      (apt.therapistName && apt.therapistName.toLowerCase().includes(lowerTerm)) ||
      (apt.treatmentGroupName && apt.treatmentGroupName.toLowerCase().includes(lowerTerm))
    );
  }, [allAppointments, searchTerm]);

  // Filter appointments by date range
  const filterByDateRange = React.useCallback(
    (start: Date, end: Date): IAppointment[] => {
      const startStr = formatDateString(start);
      const endStr = formatDateString(end);
      return allAppointments.filter((apt) => {
        return apt.date >= startStr && apt.date <= endStr;
      });
    },
    [allAppointments]
  );

  // Filter by therapist search
  const filterByPatientSearch = React.useCallback(
    (searchTerm?: string): IAppointment[] => {
      if (!searchTerm) return allAppointments;
      const lowerSearch = searchTerm.toLowerCase();
      return allAppointments.filter((apt) =>
        apt.therapistName.toLowerCase().includes(lowerSearch)
      );
    },
    [allAppointments]
  );

  // Combined filter function
  const filterAppointments = React.useCallback(
    (options: {
      dateRange?: { start: Date; end: Date };
      fundingSourceIds?: number[];
      therapistSearch?: string;
    }): IAppointment[] => {
      let filtered = allAppointments;

      if (options.dateRange) {
        const startStr = formatDateString(options.dateRange.start);
        const endStr = formatDateString(options.dateRange.end);
        filtered = filtered.filter(
          (apt) => apt.date >= startStr && apt.date <= endStr
        );
      }

      // Filter by funding sources (if needed)
      if (options.fundingSourceIds && options.fundingSourceIds.length > 0) {
        filtered = filtered.filter((apt) => {
          if (!apt.fundingSourcesId) {
            return options.fundingSourceIds!.includes(0);
          }
          return options.fundingSourceIds!.includes(apt.fundingSourcesId);
        });
      }

      if (options.therapistSearch) {
        const lowerSearch = options.therapistSearch.toLowerCase();
        filtered = filtered.filter((apt) =>
          apt.therapistName.toLowerCase().includes(lowerSearch)
        );
      }

      return filtered;
    },
    [allAppointments]
  );

  const value = {
    // Until the answer arrives, booking stays available: the server refuses it anyway, and
    // hiding the option on a slow request would look like the feature had been taken away.
    canSelfBook: selfBooking?.canSelfBook !== false,
    allAppointments,
    searchedAppointments,
    isLoading: false, // No loading since data is from Redux store
    searchTerm,
    setSearchTerm,
    filterByDateRange,
    filterByPatientSearch,
    filterAppointments,
  };

  return (
    <AppointmentsContext.Provider value={value}>
      {children}
    </AppointmentsContext.Provider>
  );
};

/**
 * Hook to access appointments context
 * Must be used within AppointmentsProvider
 */
export const useAppointmentsContext = (): AppointmentsContextValue => {
  const context = useContext(AppointmentsContext);
  if (!context) {
    throw new Error(
      "useAppointmentsContext must be used within AppointmentsProvider"
    );
  }
  return context;
};
